import type { OnboardingStep } from './machineryDirectiveOnboardingTypes'

export type MachineryDirectiveOnboardingSidebarStepId =
  | 'intro'
  | 'e_plan'
  | 'high_risk'
  | 'machine_completion'
  | 'machine_locations'
  | 'standards'
  | 'type_b_questionnaire'

export const MACHINERY_DIRECTIVE_ONBOARDING_SIDEBAR_STEPS: MachineryDirectiveOnboardingSidebarStepId[] =
  [
    'intro',
    'e_plan',
    'high_risk',
    'machine_completion',
    'machine_locations',
    'standards',
    'type_b_questionnaire',
  ]

const SIDEBAR_STEP_TO_ONBOARDING_STEP: Record<
  Exclude<MachineryDirectiveOnboardingSidebarStepId, 'high_risk'>,
  OnboardingStep
> = {
  intro: 'intro',
  e_plan: 'e_plan',
  machine_completion: 'machine_completion',
  machine_locations: 'machine_locations',
  standards: 'standards',
  type_b_questionnaire: 'type_b_questionnaire',
}

export type HighRiskStepNavigationContext = {
  highRiskCheckedAt: string | null | undefined
  hasInSessionHighRiskResult: boolean
  userElevatedHighRiskLocked: boolean
  automatedHighRiskLocked: boolean
}

export function resolveHighRiskOnboardingStep(
  context: HighRiskStepNavigationContext
): OnboardingStep {
  if (context.userElevatedHighRiskLocked) {
    return 'user_high_risk_override_locked'
  }
  if (
    context.highRiskCheckedAt != null ||
    context.hasInSessionHighRiskResult
  ) {
    return 'high_risk_result'
  }
  return 'high_risk'
}

export function sidebarStepIdToOnboardingStep(
  stepId: MachineryDirectiveOnboardingSidebarStepId,
  highRiskContext: HighRiskStepNavigationContext
): OnboardingStep {
  if (stepId === 'high_risk') {
    return resolveHighRiskOnboardingStep(highRiskContext)
  }
  return SIDEBAR_STEP_TO_ONBOARDING_STEP[stepId]
}

export function getPreviousSidebarStepId(
  stepId: MachineryDirectiveOnboardingSidebarStepId
): MachineryDirectiveOnboardingSidebarStepId | null {
  const index = getMachineryDirectiveOnboardingSidebarStepIndex(stepId)
  if (index <= 0) {
    return null
  }
  return MACHINERY_DIRECTIVE_ONBOARDING_SIDEBAR_STEPS[index - 1] ?? null
}

export function getNextSidebarStepId(
  stepId: MachineryDirectiveOnboardingSidebarStepId
): MachineryDirectiveOnboardingSidebarStepId | null {
  const index = getMachineryDirectiveOnboardingSidebarStepIndex(stepId)
  if (index >= MACHINERY_DIRECTIVE_ONBOARDING_SIDEBAR_STEPS.length - 1) {
    return null
  }
  return MACHINERY_DIRECTIVE_ONBOARDING_SIDEBAR_STEPS[index + 1] ?? null
}

export function isSidebarStepCompleteForProgress(
  stepId: MachineryDirectiveOnboardingSidebarStepId,
  openedStepId: MachineryDirectiveOnboardingSidebarStepId,
  furthestProgressStepId: MachineryDirectiveOnboardingSidebarStepId
): boolean {
  const stepIndex = getMachineryDirectiveOnboardingSidebarStepIndex(stepId)
  const openedIndex =
    getMachineryDirectiveOnboardingSidebarStepIndex(openedStepId)
  const furthestIndex = getMachineryDirectiveOnboardingSidebarStepIndex(
    furthestProgressStepId
  )

  if (stepIndex < openedIndex) {
    return true
  }
  if (stepIndex > openedIndex && stepIndex < furthestIndex) {
    return true
  }
  return false
}

export function isSidebarStepNavigable(
  stepId: MachineryDirectiveOnboardingSidebarStepId,
  furthestProgressStepId: MachineryDirectiveOnboardingSidebarStepId
): boolean {
  return (
    getMachineryDirectiveOnboardingSidebarStepIndex(stepId) <=
    getMachineryDirectiveOnboardingSidebarStepIndex(furthestProgressStepId)
  )
}

export function getMachineryDirectiveOnboardingSidebarStepId(
  onboardingStep: OnboardingStep
): MachineryDirectiveOnboardingSidebarStepId {
  if (onboardingStep === 'intro') {
    return 'intro'
  }
  if (onboardingStep === 'e_plan') {
    return 'e_plan'
  }
  if (
    onboardingStep === 'high_risk' ||
    onboardingStep === 'high_risk_result' ||
    onboardingStep === 'user_high_risk_override_locked'
  ) {
    return 'high_risk'
  }
  if (onboardingStep === 'machine_completion') {
    return 'machine_completion'
  }
  if (onboardingStep === 'machine_locations') {
    return 'machine_locations'
  }
  if (onboardingStep === 'standards') {
    return 'standards'
  }
  if (onboardingStep === 'type_b_questionnaire') {
    return 'type_b_questionnaire'
  }
  return 'intro'
}

export function getMachineryDirectiveOnboardingSidebarStepIndex(
  stepId: MachineryDirectiveOnboardingSidebarStepId
): number {
  return MACHINERY_DIRECTIVE_ONBOARDING_SIDEBAR_STEPS.indexOf(stepId)
}

export function isMachineryDirectiveOnboardingSidebarStepComplete(
  stepId: MachineryDirectiveOnboardingSidebarStepId,
  currentStepId: MachineryDirectiveOnboardingSidebarStepId
): boolean {
  return (
    getMachineryDirectiveOnboardingSidebarStepIndex(stepId) <
    getMachineryDirectiveOnboardingSidebarStepIndex(currentStepId)
  )
}
